import { atom, useAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";

// Global counter for spinner IDs
let spinnerIdCounter = 0;

// Global atom for spinner reference count (for manual API)
const spinnerRefCountAtom = atom<number>(0);

// Global atom for spinner active ids (for auto API)
const spinnerActiveIdsAtom = atom<Set<string>>(new Set<string>());

// Derived atom for spinner visibility
const isSpinnerVisibleAtom = atom((get) => {
	const refCount = get(spinnerRefCountAtom);
	const activeIds = get(spinnerActiveIdsAtom);
	const isVisible = refCount > 0 || activeIds.size > 0;
	return isVisible;
});

/**
 * Improved global spinner hook with reference counting and id-based tracking
 * Prevents race conditions by tracking active spinner ids for auto, ref count for manual
 */
export function useGlobalSpinner() {
	const [, setRefCount] = useAtom(spinnerRefCountAtom);
	const [, setActiveIds] = useAtom(spinnerActiveIdsAtom);
	const [isLoading] = useAtom(isSpinnerVisibleAtom);
	const activeRef = useRef(false);

	const addSpinner = useCallback(
		(id: string) => {
			setActiveIds((ids: Set<string>) => new Set(ids).add(id));
		},
		[setActiveIds],
	);

	const removeSpinner = useCallback(
		(id: string) => {
			setActiveIds((ids: Set<string>) => {
				const newIds = new Set(ids);
				newIds.delete(id);
				return newIds;
			});
		},
		[setActiveIds],
	);

	const showSpinner = useCallback(() => {
		activeRef.current = true;
		setRefCount((count: number) => count + 1);
	}, [setRefCount]);

	const hideSpinner = useCallback(
		(id?: string) => {
			if (id) {
				removeSpinner(id);
			} else {
				activeRef.current = false;
				setRefCount((count: number) => Math.max(0, count - 1));
			}
		},
		[removeSpinner, setRefCount],
	);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (activeRef.current) {
				setRefCount((count: number) => Math.max(0, count - 1));
			}
		};
	}, [setRefCount]);

	return {
		isLoading,
		showSpinner,
		hideSpinner,
		addSpinner,
		removeSpinner,
	};
}

/**
 * Hook that automatically manages spinner for a loading state
 * Usage: useAutoSpinner(someLoadingState)
 *
 * This is the recommended way to use the global spinner as it prevents
 * race conditions and automatically handles cleanup.
 */
export function useAutoSpinner(loading: boolean, minDuration = 300) {
	const { addSpinner, removeSpinner } = useGlobalSpinner();
	const idRef = useRef<string>(`spinner-${Date.now()}-${++spinnerIdCounter}`);
	const hideTimeoutRef = useRef<NodeJS.Timeout>();

	useEffect(() => {
		const id = idRef.current;
		if (loading) {
			// Clear any pending hide timeout
			if (hideTimeoutRef.current) {
				clearTimeout(hideTimeoutRef.current);
				hideTimeoutRef.current = undefined;
			}
			addSpinner(id);
		} else {
			// Delay hiding the spinner to ensure it's visible for at least minDuration
			hideTimeoutRef.current = setTimeout(() => {
				removeSpinner(id);
			}, minDuration);
		}
	}, [loading, addSpinner, removeSpinner, minDuration]);

	// Cleanup on unmount
	useEffect(() => {
		const id = idRef.current;
		return () => {
			if (hideTimeoutRef.current) {
				clearTimeout(hideTimeoutRef.current);
			}
			removeSpinner(id);
		};
	}, [removeSpinner]);
}
